import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@siliconx/shared/ui/Card';
import { Button } from '@siliconx/shared/ui/Button';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import type { BenchCollectResultView, BenchCollectStageView, BenchRunDetail } from '@siliconx/shared';
import { getBenchCollectStages, recordBenchCollectedResult } from '../../lib/bench';
import { qk } from '../../lib/queryKeys';
import { AgentText } from './AgentText';

export const OPERATOR_NOTE_MAX = 1000;
/** A recorded fail or timeout (or error) that is being changed to pass must say why: the database refuses it otherwise. */
const FAILED = new Set(['fail', 'timeout', 'error']);

const ERROR_COPY: Record<string, string> = {
  note_required: 'Say why a recorded failure is now a pass: add a note.',
  collect_disabled: 'Recording results by hand is not enabled.',
  run_not_running: 'This run is no longer running.',
  run_expired: 'This run reached its 24 hour limit.',
  collected_cap: 'This test has reached its limit of recorded results.',
  not_found: 'This run was not found.',
  invalid_argument: 'The result could not be recorded as entered. Remove unusual characters from the note.',
  payload_too_large: 'The note is too long.',
};

/** The API's error `code` (carried on the thrown error's body) picks the copy. */
function errorMessage(err: unknown): string {
  const e = err as { message?: string; body?: { error?: { code?: string } } } | null;
  const code = e?.body?.error?.code ?? e?.message ?? '';
  const key = Object.keys(ERROR_COPY).find((k) => code.includes(k));
  return key ? ERROR_COPY[key]! : 'The result could not be recorded. Try again.';
}

export function latestResult(stage: BenchCollectStageView): BenchCollectResultView | null {
  return stage.results.find((r) => r.isLatest) ?? stage.results.at(-1) ?? null;
}

/** Whether a note is mandatory for this entry (mirrors the database rule; UX only, the server decides). */
export function noteRequired(stage: BenchCollectStageView, status: 'pass' | 'fail'): boolean {
  const latest = latestResult(stage);
  return latest !== null && FAILED.has(latest.status) && status === 'pass';
}

const STATUS_TONE: Record<string, 'green' | 'red' | 'neutral'> = { pass: 'green', fail: 'red' };

/**
 * SIL-1719 (E-33): the technician records pass/fail for a `collect: operator` test (a tool that ran outside the bench's own
 * operating system, spec 7.2 option 3). Shown only while the run is running and the API reports the feature enabled.
 *
 * What this is NOT: a certificate source. A hand-entered result can only lower a grade, never raise it; a pass changes nothing
 * and is labelled so. The whole history of a test is shown (who is not named, only "you" or "another member"), because a
 * correction replaces the earlier result in the grade while every earlier entry stays on record. All notes are rendered as
 * text through AgentText (never as HTML). The server re-checks everything (membership, run state, the test, the class).
 */
export function OperatorEntryPanel({ run }: { run: BenchRunDetail }) {
  const enabled = run.status === 'running';
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: qk.bench.collectStages(run.id),
    queryFn: () => getBenchCollectStages(run.id),
    enabled,
    refetchInterval: enabled ? 10_000 : false,
  });
  if (!enabled || list.isPending || list.isError || !list.data.enabled || list.data.stages.length === 0) return null;
  return (
    <Card className="space-y-4">
      <PanelTitle
        title="Tests recorded by hand"
        description="Record the result of a test that ran outside the bench's own software. A failure lowers the grade. A pass is recorded but never improves it."
      />
      <ul className="space-y-4">
        {list.data.stages.map((stage) => (
          <li key={`${stage.profileId}:${stage.stageIndex}`}>
            <StageEntry
              runId={run.id}
              stage={stage}
              onRecorded={() => {
                void queryClient.invalidateQueries({ queryKey: qk.bench.collectStages(run.id) });
                void queryClient.invalidateQueries({ queryKey: qk.bench.run(run.id) });
              }}
            />
          </li>
        ))}
      </ul>
    </Card>
  );
}

function StageEntry({ runId, stage, onRecorded }: { runId: string; stage: BenchCollectStageView; onRecorded: () => void }) {
  const [status, setStatus] = useState<'pass' | 'fail'>('fail');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const idBase = `op-${stage.profileId}-${stage.stageIndex}`.replace(/[^A-Za-z0-9_-]/g, '_');
  const mutation = useMutation({
    mutationFn: () => recordBenchCollectedResult(runId, {
      profileId: stage.profileId,
      stageIndex: stage.stageIndex,
      status,
      ...(note.trim() === '' ? {} : { note: note.trim() }),
    }),
    onSuccess: () => {
      setNote('');
      setFormError(null);
      onRecorded();
    },
  });
  const latest = latestResult(stage);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (noteRequired(stage, status) && note.trim() === '') {
      setFormError(ERROR_COPY.note_required!);
      return;
    }
    setFormError(null);
    mutation.mutate();
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <AgentText value={stage.stageName} max={128} className="text-sm" />
        {latest ? (
          <StatusPill tone={STATUS_TONE[latest.status] ?? 'neutral'}>{`Latest: ${latest.status}`}</StatusPill>
        ) : (
          <StatusPill tone="neutral">No result yet</StatusPill>
        )}
      </div>
      {stage.results.length > 0 && (
        <ol className="space-y-1 text-xs text-muted-foreground" aria-label={`History of ${stage.stageName}`}>
          {stage.results.map((r) => (
            <li key={r.seq} className="flex flex-wrap gap-x-2">
              <span>{r.status}</span>
              <span>{r.byMe ? 'recorded by you' : 'recorded by another member'}</span>
              <time dateTime={r.recordedAt}>{new Date(r.recordedAt).toLocaleString()}</time>
              {r.isLatest && <span className="font-medium text-foreground">counts toward the grade</span>}
              {r.note && <AgentText value={r.note} max={OPERATOR_NOTE_MAX} className="text-xs" />}
            </li>
          ))}
        </ol>
      )}
      <form onSubmit={submit} className="space-y-2">
        <fieldset className="flex gap-4">
          <legend className="sr-only">Result for {stage.stageName}</legend>
          {(['fail', 'pass'] as const).map((v) => (
            <label key={v} className="flex items-center gap-1 text-sm">
              <input type="radio" name={`${idBase}-status`} value={v} checked={status === v} onChange={() => setStatus(v)} />
              {v === 'fail' ? 'Failed' : 'Passed'}
            </label>
          ))}
        </fieldset>
        <label htmlFor={`${idBase}-note`} className="block text-sm font-medium">
          Note{noteRequired(stage, status) ? ' (required: say why the failure is now a pass)' : ' (optional)'}
        </label>
        <textarea
          id={`${idBase}-note`}
          value={note}
          maxLength={OPERATOR_NOTE_MAX}
          rows={2}
          onChange={(e) => setNote(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        <p className="text-xs text-muted-foreground">
          {note.length}/{OPERATOR_NOTE_MAX}.{' '}
          {status === 'pass'
            ? 'A pass is recorded as "recorded by hand, not verified by the machine" and does not improve the grade.'
            : 'A failure lowers the grade. It can be corrected later; the earlier entry stays on record.'}
        </p>
        {formError && <p role="alert" className="text-sm text-status-red">{formError}</p>}
        {mutation.isError && <p role="alert" className="text-sm text-status-red">{errorMessage(mutation.error)}</p>}
        <Button type="submit" variant="secondary" disabled={mutation.isPending}>
          Record result
        </Button>
      </form>
    </div>
  );
}
